import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/**
 * Componente genérico para mostrar cuando una funcionalidad está próximamente disponible
 * Se usa cuando un feature flag está deshabilitado
 */
@Component({
  selector: 'app-coming-soon',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule],
  template: `
    <div class="coming-soon-container">
      <div class="coming-soon-content">
        <!-- Illustration -->
        <div class="illustration">
          <div class="rocket-icon">🚀</div>
        </div>

        <!-- Title -->
        <h1 class="coming-soon-title">
          {{ 'COMING_SOON.TITLE' | translate }}
        </h1>

        <!-- Description -->
        <p class="coming-soon-description">
          {{ featureName }}
        </p>

        <!-- Secondary description -->
        <p class="coming-soon-subtitle">
          {{ 'COMING_SOON.SUBTITLE' | translate }}
        </p>

        <!-- Features list (optional) -->
        <div class="features-teaser" *ngIf="features && features.length > 0">
          <h3>{{ 'COMING_SOON.FEATURES_PREVIEW' | translate }}</h3>
          <ul class="features-list">
            <li *ngFor="let feature of features">
              <mat-icon>check_circle</mat-icon>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>

        <!-- CTA Buttons -->
        <div class="cta-buttons">
          <button mat-raised-button color="primary" (click)="goHome()">
            <mat-icon>home</mat-icon>
            {{ 'COMING_SOON.GO_HOME' | translate }}
          </button>
          <button mat-stroked-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            {{ 'COMING_SOON.GO_BACK' | translate }}
          </button>
        </div>

        <!-- Notification signup (optional) -->
        <div class="notification-section">
          <p class="notification-text">
            {{ 'COMING_SOON.NOTIFICATION_TEXT' | translate }}
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .coming-soon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: 2rem;
    }

    .coming-soon-content {
      background: white;
      border-radius: 16px;
      padding: 4rem 2rem;
      max-width: 600px;
      width: 100%;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      animation: slideUp 0.6s ease-out;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .illustration {
      margin-bottom: 2rem;
    }

    .rocket-icon {
      font-size: 80px;
      animation: bounce 2s infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-20px);
      }
    }

    .coming-soon-title {
      font-size: 2.5rem;
      font-weight: 700;
      margin: 0 0 1rem 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .coming-soon-description {
      font-size: 1.2rem;
      color: #333;
      margin: 0.5rem 0 1rem 0;
      font-weight: 600;
    }

    .coming-soon-subtitle {
      font-size: 1rem;
      color: #666;
      margin: 1.5rem 0;
      line-height: 1.6;
    }

    .features-teaser {
      margin: 2rem 0;
      text-align: left;
      background: #f8f9fa;
      padding: 1.5rem;
      border-radius: 12px;
    }

    .features-teaser h3 {
      margin: 0 0 1rem 0;
      color: #333;
      font-size: 1.1rem;
    }

    .features-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .features-list li {
      display: flex;
      align-items: center;
      margin: 0.75rem 0;
      color: #555;
    }

    .features-list mat-icon {
      margin-right: 0.75rem;
      color: #667eea;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .cta-buttons {
      display: flex;
      gap: 1rem;
      margin: 2.5rem 0;
      justify-content: center;
      flex-wrap: wrap;
    }

    .cta-buttons button {
      min-width: 150px;
      padding: 0.75rem 1.5rem;
    }

    .notification-section {
      margin-top: 2rem;
      padding-top: 2rem;
      border-top: 1px solid #eee;
    }

    .notification-text {
      font-size: 0.9rem;
      color: #888;
      margin: 0;
    }

    @media (max-width: 600px) {
      .coming-soon-content {
        padding: 2rem 1rem;
      }

      .coming-soon-title {
        font-size: 2rem;
      }

      .rocket-icon {
        font-size: 60px;
      }

      .cta-buttons {
        flex-direction: column;
      }

      .cta-buttons button {
        width: 100%;
      }
    }
  `]
})
export class ComingSoonComponent {
  @Input() featureName: string = 'Feature';
  @Input() features: string[] | null = null;

  private router = inject(Router);
  private translateService = inject(TranslateService);

  goHome(): void {
    this.router.navigate(['/home']);
  }

  goBack(): void {
    window.history.back();
  }
}
